import { ActionIcon, Card, Group, Progress, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { MantineReactTable } from 'mantine-react-table';
import type { TableBasicRow } from './LazyTable.types';
import { LazyTableSetting } from './LazyTableSettings';
import { type LazyTableProps, useLazyTable } from './useLazyTable';

export type TableFilterProps =
  | { children?: undefined; resetFilter?: undefined }
  | { children: React.ReactNode; resetFilter: () => void };

export function LazyTable<TRow extends TableBasicRow>(
  props: LazyTableProps<TRow> & TableFilterProps
) {
  const {
    columns,
    hideSettings = false,
    hideTopBar = false,
    newTableLayout = false,
  } = props.table;

  const table = useLazyTable(props);

  return (
    <>
      {!hideTopBar && (!hideSettings || !!props.children) && (
        <Card
          bg={!newTableLayout ? 'transparent' : 'gray.0'}
          px="sm"
          py="xs"
          radius="none"
          withBorder
          sx={{
            borderLeft: 0,
            borderRight: 0,
            borderTop: 0,
            borderRadius: 0,
          }}
        >
          <Group align="start" gap="md">
            <Group flex={1} miw={0} gap="xs">
              {props.children}
            </Group>

            <Group gap="xs">
              <Tooltip label="Reset Filter" withArrow>
                <ActionIcon
                  variant="transparent"
                  color="neutral.5"
                  title="Reset"
                  onClick={props.resetFilter}
                >
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>

              {!hideSettings && (
                <LazyTableSetting
                  table={table}
                  columnDefs={columns}
                  tableKey={props.table.key}
                  canGroup={!!props.groupBy?.groupBy}
                  resetFilter={props.resetFilter}
                  sortOptions={props.sorting?.options}
                />
              )}
            </Group>
          </Group>
        </Card>
      )}
      {typeof props.data.isFetching === 'boolean' && (
        <Progress
          value={100}
          striped
          size={props.table.progressBarSize}
          animated
          styles={{
            root: {
              opacity: props.data.isFetching && !props.data.loading ? '1' : '0',
              transition: 'opacity 400ms ease',
            },
          }}
        />
      )}
      <MantineReactTable table={table} />
    </>
  );
}
